import { Alert, Button, Center, Loader, Stack, Text } from "@mantine/core";
import { signOut } from "aws-amplify/auth";
import { rateLimitedFetchAuthSession } from "./fetchAuthSession";
// src/App.tsx
import React, { Suspense, useEffect, useState } from "react";
import {
	Navigate,
	Outlet,
	Route,
	Routes,
	createSearchParams,
	useLocation,
	useNavigate,
} from "react-router";

import { DocumentTitle } from "@/components/DocumentTitle";
import { NavigationProgressHandler } from "@/components/NavigationProgressHandler";
import { UniversalLayout } from "@/components/UniversalLayout";
import { ColorModeForcer } from "@/components/color-mode-forcer";
import { getActionQueryOptions } from "./api/getActionQueryOptions";
import * as SentryReact from "@sentry/react";
import ErrorBoundary, { ErrorFallback } from "@/components/ErrorBoundary";
import { setSentryUser } from "@/common/utils/sentry";
import { MagicBox } from "@/components/MagicBox";
import { NotFound } from "@/components/NotFound";

import { ROUTES } from "./routes";
import { useQueryClient } from "@tanstack/react-query";
import DeepLinkListener from "./DeepLinkListener";
import { SystemBarsColorSchemeSync } from "./SystemBarsColorSchemeSync";
import { PushNotificationsReRegisterer } from "@/notifications/components/PushNotificationsReRegisterer";
import { PushNotificationsRegistrationListener } from "@/notifications/components/PushNotificationsRegistrationListener";
import { PushNotificationActionPerformedListener } from "@/notifications/components/PushNotificationActionPerformedListener";
import ClientFilePreviewPage from "@/clients/pages/ClientFilePreviewPage";
import CaregiverFilePreviewPage from "@/caregivers/pages/CaregiverFilePreviewPage";
import { CaregiverAppLayout } from "@/caregiver-app/components/CaregiverAppLayout";
import { OnboardingGuard } from "@/caregiver-app/components/OnboardingGuard";
import { OfflineDataSync } from "./OfflineDataSync";
import { isNetworkError } from "@/common/utils/is-network-error";
import { useCacheStorage } from "./storage/CacheStorageContext";
import { useLocalAuth } from "./useLocalAuth";
import { PushNotificationsPrompter } from "@/notifications/components/PushNotificationsPrompter";
import { MFAEnforcementModal } from "@/auth/components/MFAEnforcementModal";
import { UserSMSConsentModal } from "@/notifications/components/UserSMSConsentModal";
import type { ActionOutput } from "../backend/types";
import { useGetCurrentUserQuery } from "./api/apiHooks";
import { modals } from "@mantine/modals";
import { useLogger } from "@/logging/client/LoggerContext";
import CaregiverAppCarePlanPage from "@/caregiver-app/pages/CaregiverAppCarePlanPage";
import CaregiverAppCareSheetIndexPage from "@/caregiver-app/pages/CaregiverAppCareSheetIndexPage";
import CaregiverAppCareSheetReviewPage from "@/caregiver-app/pages/CaregiverAppCareSheetReviewPage";
import CaregiverAppCareSheetShiftCheckoutPage from "@/caregiver-app/pages/CaregiverAppCareSheetShiftCheckoutPage";
import CaregiverAppFormDetailPage from "@/caregiver-app/pages/CaregiverAppFormDetailPage";
import CaregiverAppFormsIndexPage from "@/caregiver-app/pages/CaregiverAppFormsIndexPage";
import CaregiverAppIndexPage from "@/caregiver-app/pages/CaregiverAppIndexPage";
import CaregiverAppOnboardingPage from "@/caregiver-app/pages/CaregiverAppOnboardingPage";
import CaregiverAppPickupPage from "@/caregiver-app/pages/CaregiverAppPickupPage";

// Auth Guard component to handle remote authentication (i.e. with the server)
function RemoteAuthGuard({ children }: { children: React.ReactNode }) {
	const queryClient = useQueryClient();
	const location = useLocation();
	const navigate = useNavigate();
	const [currentUser, setCurrentUser] =
		useState<ActionOutput<"getCurrentUser"> | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const cacheStorage = useCacheStorage();
	const logger = useLogger();

	useEffect(() => {
		const getCurrentUserOptions = getActionQueryOptions(
			"getCurrentUser",
			undefined,
			{
				batchEnabled: false,
			},
		);

		const checkAuth = async () => {
			try {
				// First, check if we have cached user data and use it immediately
				let cachedUser = null;
				if (cacheStorage.isAvailable()) {
					const cachedUserString = await cacheStorage
						.getItem("vcg-current-user")
						.catch(() => null);
					if (cachedUserString) {
						try {
							cachedUser = JSON.parse(cachedUserString);
							setCurrentUser(cachedUser);
							setIsLoading(false);
						} catch (parseError) {
							// Invalid cached data, remove it
							cacheStorage.removeItem("vcg-current-user");
						}
					}
				}

				if (cachedUser) {
					// set the cached user data in the query client
					queryClient.setQueryData(getCurrentUserOptions.queryKey, cachedUser, {
						// set the updatedAt to the last modified time of the cached user data
						// this is used to determine if the cached data is stale
						updatedAt: new Date(
							(await cacheStorage.getLastModified("vcg-current-user")) ?? 0,
						).getTime(),
					});
				}

				// Fetch fresh user data in the background
				const user = await queryClient
					.ensureQueryData({
						...getCurrentUserOptions,
						queryFn: () =>
							(
								getCurrentUserOptions.queryFn as () => Promise<
									ActionOutput<"getCurrentUser">
								>
							)()
								.then((user) => {
									if (user) {
										cacheStorage.setItem(
											"vcg-current-user",
											JSON.stringify(user),
										);
									} else {
										cacheStorage.removeItem("vcg-current-user");
									}

									return user;
								})
								.catch((e) => {
									if (isNetworkError(e) && cacheStorage.isAvailable()) {
										return cacheStorage
											.getItem("vcg-current-user")
											.then((user) => (user ? JSON.parse(user) : null));
									}

									if (cacheStorage.isAvailable()) {
										cacheStorage.removeItem("vcg-current-user");
									}

									return null;
								}),
						staleTime: 1000 * 60 * 5, // 5 minutes
						networkMode: "always",
						retry: 1,
						revalidateIfStale: true, // get the latest if stale
					})
					.catch(() => {
						return null;
					});

				// Update with fresh data (might be the same as cached data)
				setCurrentUser(user);
				setIsLoading(false);
				setSentryUser({
					email: user?.email,
					id: user?.id,
					username: user?.username,
				});

				// Handle navigation logic
				const userForNavigation = user || cachedUser;

				if (userForNavigation) {
					logger.info(
						`RemoteAuthGuard: navigating to ${location.pathname}${location.search}`,
					);
				}

				if (!userForNavigation && !location.pathname.startsWith("/login")) {
					logger.info(
						`RemoteAuthGuard: no authenticated user, redirecting to login from ${location.pathname}`,
					);
					await signOut().catch(() => {});
					navigate(
						`/login?redirect=${encodeURIComponent(
							`${location.pathname}${location.search}`,
						)}`,
					);
				} else if (
					userForNavigation &&
					location.pathname.startsWith("/login")
				) {
					logger.info(
						"RemoteAuthGuard: user already authenticated, redirecting from login to /",
					);
					navigate("/");
				}

				if (
					(userForNavigation?.isClient ||
						userForNavigation?.isTrustedContact) &&
					!location.pathname.startsWith("/client-mobile-app")
				) {
					logger.info(
						"RemoteAuthGuard: client/trusted contact role redirect to /client-mobile-app",
					);
					navigate("/client-mobile-app");
				}
				if (
					userForNavigation?.isCaregiver &&
					!location.pathname.startsWith("/app") &&
					!location.pathname.startsWith("/chat")
				) {
					logger.info("RemoteAuthGuard: caregiver role redirect to /app");
					navigate("/app");
				}
			} catch (error) {
				setIsLoading(false);
				setCurrentUser(null);
			}
		};

		// Verify the auth session has a valid token after checkAuth completes.
		// Retry with skipCache before invalidating to avoid a race where another tab's
		// in-flight token refresh temporarily leaves the session without tokens.
		checkAuth().finally(() => {
			rateLimitedFetchAuthSession().then((session) => {
				if (!session.tokens?.accessToken) {
					logger.warn(
						"RemoteAuthGuard: access token missing after auth session fetch, retrying with skipCache",
					);
					rateLimitedFetchAuthSession({ skipCache: true }).then(
						(retrySession) => {
							if (!retrySession.tokens?.accessToken) {
								logger.warn(
									"RemoteAuthGuard: access token still missing after retry, re-checking auth",
								);
								cacheStorage.removeItem("vcg-current-user");
								queryClient.setQueryData(getCurrentUserOptions.queryKey, null);
								checkAuth();
							}
						},
					);
				}
			});
		});
	}, [location.pathname, location.search, navigate]);

	if (isLoading) {
		return (
			<MagicBox column grow center>
				<Loader type="bars" />
			</MagicBox>
		);
	}

	if (!currentUser && !location.pathname.startsWith("/login")) {
		const searchParams = createSearchParams({
			redirect: location.pathname + location.search,
		});
		return (
			<Navigate
				to={`${ROUTES.LOGIN.ROOT}?${searchParams.toString()}`}
				replace
			/>
		);
	}

	if (currentUser && location.pathname.startsWith("/login")) {
		return <Navigate to={ROUTES.HOME.ROOT} replace />;
	}

	if (
		(currentUser?.isClient || currentUser?.isTrustedContact) &&
		!location.pathname.startsWith("/client-mobile-app")
	) {
		return <Navigate to="/client-mobile-app" replace />;
	}

	if (
		currentUser?.isCaregiver &&
		!location.pathname.startsWith("/app") &&
		!location.pathname.startsWith("/chat")
	) {
		return <Navigate to="/app" replace />;
	}

	return children;
}

// Loading component for route suspense
function LoadingFallback() {
	return (
		<MagicBox column grow center>
			<Loader type="bars" />
		</MagicBox>
	);
}

function RootLayout({ children }: { children: React.ReactNode }) {
	const content = (
		<>
			{children}
			<ColorModeForcer />
			<SystemBarsColorSchemeSync />
		</>
	);

	if (import.meta.env.VITE_SENTRY_DSN) {
		return (
			<ErrorBoundary>
				<SentryReact.ErrorBoundary fallback={ErrorFallback}>
					{content}
				</SentryReact.ErrorBoundary>
			</ErrorBoundary>
		);
	}

	return <ErrorBoundary>{content}</ErrorBoundary>;
}

function MainLayout() {
	return (
		<UniversalLayout>
			<Outlet />
		</UniversalLayout>
	);
}

function PreferedRouteRedirector() {
	const navigate = useNavigate();
	const location = useLocation();
	const currentUser = useGetCurrentUserQuery();
	const isCaregiver = currentUser.data?.isCaregiver;
	const isClient = currentUser.data?.isClient;
	const isTrustedContact = currentUser.data?.isTrustedContact;
	const isOther = !isCaregiver && !isClient && !isTrustedContact;

	useEffect(() => {
		if (
			currentUser.data &&
			isOther &&
			import.meta.env.VITE_CAPACITOR_BUILD === "true" &&
			location.pathname === "/" &&
			location.search === ""
		) {
			// we want the default landing route to be care forms for backoffice users on the mobile app
			navigate("/care-forms");
		}
	}, [isOther, currentUser.data]);

	return null;
}

function AppStoreUpdatePrompter() {
	useEffect(() => {
		if (import.meta.env.VITE_CAPACITOR_BUILD !== "true") {
			return;
		}

		async function check() {
			const { AppUpdate, AppUpdateAvailability } = await import(
				"@capawesome/capacitor-app-update"
			);
			const result = await AppUpdate.getAppUpdateInfo().catch(() => null);

			if (
				result?.updateAvailability === AppUpdateAvailability.UPDATE_AVAILABLE
			) {
				modals.openConfirmModal({
					title: "Update Available",
					children: (
						<Text size="md">
							A new version of the app is available. Please update to continue
							having the best experience and to ensure the security of your
							data.
						</Text>
					),
					labels: {
						confirm: "Open App Store",
						cancel: "I will update later",
					},
					onConfirm: () => {
						AppUpdate.openAppStore({
							appId: "6745334615",
						});
					},
					centered: true,
				});
			}
		}

		check();
	}, []);

	return null;
}

export default function App() {
	const localAuth = useLocalAuth();

	if (localAuth.loading || localAuth.isAuthenticated === null) {
		return (
			<Center w="100%" h="100%">
				<Loader type="oval" size="lg" />
			</Center>
		);
	}

	if (localAuth.error) {
		const requiresInteraction = localAuth.error.message
			?.toLowerCase()
			.includes("interaction required");

		return (
			<Center w="100%" h="100%">
				<Stack align="center">
					<Alert title="Authentication Error" color="red">
						{localAuth.error.message}
					</Alert>
					{requiresInteraction && (
						<Button size="md" onClick={() => localAuth.prompt?.()}>
							Unlock with Face ID / Touch ID / Passcode
						</Button>
					)}
				</Stack>
			</Center>
		);
	}

	if (!localAuth.isAuthenticated) {
		return (
			<Center w="100%" h="100%">
				<Alert title="Authentication Failed" color="red">
					Failed to authenticate you with your device.
				</Alert>
			</Center>
		);
	}

	return (
		<RootLayout>
			<DocumentTitle />
			<DeepLinkListener />
			<PushNotificationsRegistrationListener />
			<PushNotificationActionPerformedListener />
			<NavigationProgressHandler />
			<AppStoreUpdatePrompter />
			<Suspense fallback={<LoadingFallback />}>
				<Routes>
					<Route
						path={ROUTES.LOGIN.ROOT}
						element={
							<Suspense fallback={<LoadingFallback />}>
								<LoginPage />
							</Suspense>
						}
					/>

					<Route
						element={
							<RemoteAuthGuard>
								<OfflineDataSync />
								<PushNotificationsReRegisterer />
								<PushNotificationsPrompter />
								{!import.meta.env.VITE_HIDE_MFA_ENFORCEMENT_MODAL && (
									<MFAEnforcementModal />
								)}
								<UserSMSConsentModal />
								<PreferedRouteRedirector />
								<Outlet />
							</RemoteAuthGuard>
						}
					>
						<Route
							path={"/client-mobile-app"}
							element={<ClientMobileAppHomePage />}
						/>
						<Route path={ROUTES.CAREGIVER_APP.ROOT}>
							<Route
								path="onboarding"
								element={<CaregiverAppOnboardingPage />}
							/>
							<Route
								element={
									<OnboardingGuard>
										<CaregiverAppLayout>
											<Outlet />
										</CaregiverAppLayout>
									</OnboardingGuard>
								}
							>
								<Route index element={<CaregiverAppIndexPage />} />
								<Route
									path={ROUTES.CAREGIVER_APP.PICKUP}
									element={<CaregiverAppPickupPage />}
								/>
								<Route path={ROUTES.CAREGIVER_APP.CARE_SHEETS.ROOT}>
									<Route index element={<CaregiverAppCareSheetIndexPage />} />
								</Route>
								<Route path={ROUTES.CAREGIVER_APP.FORMS.ROOT}>
									<Route index element={<CaregiverAppFormsIndexPage />} />
								</Route>
							</Route>
							<Route path={ROUTES.CAREGIVER_APP.CARE_SHEETS.ROOT}>
								<Route index element={<CaregiverAppCareSheetIndexPage />} />
								<Route
									path=":id/review"
									element={<CaregiverAppCareSheetReviewPage />}
								/>
								<Route
									path=":care_sheet_id/shifts/:shift_id/checkout"
									element={<CaregiverAppCareSheetShiftCheckoutPage />}
								/>
							</Route>
							<Route path={ROUTES.CAREGIVER_APP.FORMS.ROOT}>
								<Route path="new" element={<CaregiverAppFormDetailPage />} />
								<Route
									path=":form_id"
									element={<CaregiverAppFormDetailPage />}
								/>
							</Route>
							<Route
								path="care-plans/:care_plan_id"
								element={<CaregiverAppCarePlanPage />}
							/>
						</Route>
						<Route element={<MainLayout />}>
							<Route index element={<HomePage />} />

							<Route path={ROUTES.MAP.ROOT} element={<MapPage />} />

							<Route
								path={ROUTES.NOTIFICATIONS.ROOT}
								element={<NotificationsPage />}
							/>

							<Route
								index
								element={<Navigate to={ROUTES.ADMIN.BRANCHES} replace />}
							/>
							<Route path={ROUTES.ADMIN.BRANCHES} element={<BranchesPage />} />
							<Route
								path="/admin/branches/:id"
								element={<BranchDetailPage />}
							/>
							<Route path={ROUTES.ADMIN.CASES} element={<CaseAdminPage />} />
							<Route path={ROUTES.ADMIN.USERS} element={<UsersPage />} />
							<Route path="/admin/users/:id" element={<UserDetailPage />} />
							<Route path={ROUTES.ADMIN.ROLES} element={<RolesPage />} />
							<Route
								path={ROUTES.ADMIN.PAYER_SOURCES}
								element={<PayerSourcesPage />}
							/>
							<Route
								path={ROUTES.FILE_EXPORTS.ROOT}
								element={<FileExportsPage />}
							/>
							<Route
								path={ROUTES.FILE_IMPORTS.ROOT}
								element={<FileImportsPage />}
							/>
							<Route
								path={ROUTES.FILE_IMPORTS.ROWS}
								element={<FileImportRowsPage />}
							/>
							<Route path={ROUTES.ADMIN.ROOT}>
								<Route
									index
									element={<Navigate to={ROUTES.ADMIN.BRANCHES} replace />}
								/>
								<Route
									path={ROUTES.ADMIN.BRANCHES}
									element={<BranchesPage />}
								/>
								<Route
									path="/admin/branches/:id"
									element={<BranchDetailPage />}
								/>
								<Route path={ROUTES.ADMIN.USERS} element={<UsersPage />} />
								<Route path="/admin/users/:id" element={<UserDetailPage />} />
								<Route path={ROUTES.ADMIN.ROLES} element={<RolesPage />} />
								<Route
									path={ROUTES.ADMIN.PAYER_SOURCES}
									element={<PayerSourcesPage />}
								/>
								<Route
									path={ROUTES.ADMIN.PAYER_SOURCE_DETAIL(":id")}
									element={<PayerSourceDetailPage />}
								/>
								<Route
									path={ROUTES.ADMIN.VA_MEDICAL_CENTERS}
									element={<VAMedicalCentersPage />}
								/>
								<Route
									path={ROUTES.ADMIN.VA_MEDICAL_CENTER_DETAIL(":id")}
									element={<VAMedicalCenterDetailPage />}
								/>
								<Route
									path={ROUTES.ADMIN.PAY_PERIOD_LOCK_TIMES}
									element={<PayPeriodLockTimesPage />}
								/>
								<Route
									path={ROUTES.ADMIN.FILE_EXPORT_TYPES}
									element={<FileExportTypesPage />}
								/>
								<Route
									path={ROUTES.ADMIN.FILE_IMPORT_TYPES}
									element={<FileImportTypesPage />}
								/>
								<Route
									path={ROUTES.ADMIN.FEATURE_FLAGS}
									element={<FeatureFlagsPage />}
								/>
							</Route>

							<Route path={ROUTES.CARE_FORMS.ROOT}>
								<Route index element={<CareFormIndexPage />} />
								<Route path=":id" element={<CareFormDetailPage />} />
								<Route path={ROUTES.CARE_FORMS.CARE_FORM_TYPES.ROOT}>
									<Route index element={<CareFormTypesPage />} />
									<Route path=":id" element={<CareFormTypeDetailPage />} />
								</Route>
							</Route>

							<Route path={ROUTES.CASES.ROOT}>
								<Route index element={<CaseIndexPage />} />
								<Route path=":id/pdf" element={<CasePdfPage />} />
							</Route>
							<Route path={ROUTES.CAREGIVERS.ROOT}>
								<Route index element={<CaregiversPage />} />
								<Route path=":id">
									<Route index element={<Navigate to="schedule" replace />} />
									<Route path="profile" element={<CaregiverProfilePage />} />
									<Route path="schedule" element={<CaregiverSchedulePage />} />
									<Route
										path="files/:fileId"
										element={<CaregiverFilePreviewPage />}
									/>
								</Route>
							</Route>

							<Route path={ROUTES.CASES.ROOT}>
								<Route index element={<CaseIndexPage />} />
								<Route path=":id/pdf" element={<CasePdfPage />} />
							</Route>

							<Route path={ROUTES.CLIENTS.ROOT}>
								<Route index element={<ClientsPage />} />
								<Route path=":id">
									<Route index element={<Navigate to="schedule" replace />} />
									<Route path="profile" element={<ClientProfilePage />} />
									<Route path="schedule" element={<ClientSchedulePage />} />
									<Route
										path="files/:fileId"
										element={<ClientFilePreviewPage />}
									/>
								</Route>
							</Route>

							<Route path="/docs/:slug" element={<DocsPage />} />

							<Route path={ROUTES.DASHBOARD.ROOT} element={<DashboardPage />} />
							<Route path="/dashboard/admin" element={<DashboardAdminPage />} />

							<Route
								path={ROUTES.DASHBOARD.ROLLUP.ROOT}
								element={<DashboardRollupPage />}
							/>

							{/* Import Detail Pages */}
							<Route
								path="/dashboard/authorizations/automated-imports/:importId"
								element={<AutomatedImportPage />}
							/>

							{/* Performance Detail Pages - rendered without dashboard navigation */}

							<Route path={ROUTES.REPORTS.ROOT}>
								<Route index element={<ReportsIndexPage />} />
								<Route
									path={ROUTES.REPORTS.ADP_EMPLOYEES}
									element={<ADPImportedEmployeesReportPage />}
								/>
								<Route
									path={ROUTES.REPORTS.BILLING}
									element={<BillingReportPage />}
								/>
								<Route
									path={ROUTES.REPORTS.CARE_SHEET_FILES}
									element={<CareSheetFilesReportPage />}
								/>
								<Route
									path={ROUTES.REPORTS.CAREGIVER_CASH_ADVANCES}
									element={<CaregiverCashAdvancesReportPage />}
								/>
								<Route
									path={ROUTES.REPORTS.CAREGIVER_CHANGES}
									element={<CaregiverChangesReportPage />}
								/>
								<Route
									path={ROUTES.REPORTS.CAREGIVER_FILES}
									element={<CaregiverFilesReportPage />}
								/>
								<Route
									path={ROUTES.REPORTS.CAREGIVER_JOURNALS}
									element={<CaregiverJournalsReportPage />}
								/>
								<Route
									path={ROUTES.REPORTS.CLIENT_AUTHORIZATIONS}
									element={<ClientAuthorizationsReportPage />}
								/>
								<Route
									path={ROUTES.REPORTS.CLIENT_FILES}
									element={<ClientFilesReportPage />}
								/>

								<Route
									path={ROUTES.REPORTS.CLIENT_JOURNALS}
									element={<ClientJournalsReportPage />}
								/>
								<Route
									path={ROUTES.REPORTS.INTEGRATION_TRANSACTION_RECORDS}
									element={<IntegrationTransactionRecordsReportPage />}
								/>
								<Route
									path={ROUTES.REPORTS.INTEGRATION_TRANSACTIONS}
									element={<IntegrationTransactionsReportPage />}
								/>
								<Route
									path={ROUTES.REPORTS.NURSE_ASSESSMENTS}
									element={<NurseAssessmentsReportPage />}
								/>
								<Route
									path={ROUTES.REPORTS.PAYROLL}
									element={<CaregiverPayrollReportPage />}
								/>
								<Route
									path={ROUTES.REPORTS.CCW_PEPST_REPORT}
									element={<CcwReportPage />}
								/>
								<Route
									path={ROUTES.REPORTS.AUTHORIZATION_REVIEW}
									element={<AuthorizationReviewPage />}
								/>
							</Route>

							<Route path="/tickets">
								<Route index element={<TicketsBoardPage />} />
								<Route path="archived" element={<TicketsArchivedPage />} />
								<Route path="import" element={<TicketsImportPage />} />
								<Route
									path="projects/:id"
									element={<TicketsProjectDetailPage />}
								/>
								<Route path="cycles/:id" element={<TicketsCycleDetailPage />} />
								<Route path=":id" element={<TicketsDetailPage />} />
							</Route>

							<Route path={ROUTES.CHAT.ROOT} element={<ChatLayout />}>
								<Route index element={<ChatPage />} />
							</Route>

							<Route path="*" element={<NotFound />} />
						</Route>
					</Route>
				</Routes>
			</Suspense>
		</RootLayout>
	);
}

// Lazy-loaded route groups
const LoginPage = React.lazy(() => import("@/auth/pages/LoginPage"));
const HomePage = React.lazy(() => import("@/shifts/pages/HomePage"));
const BranchesPage = React.lazy(() => import("@/branches/pages/BranchesPage"));
const BranchDetailPage = React.lazy(
	() => import("@/branches/pages/BranchDetailPage"),
);
const CaseIndexPage = React.lazy(() => import("@/cases/pages/CaseIndexPage"));
const CaseAdminPage = React.lazy(() => import("@/cases/pages/CaseAdminPage"));
const CasePdfPage = React.lazy(() => import("@/cases/pages/CasePdfPage"));
const CareFormTypesPage = React.lazy(
	() => import("@/care-forms/pages/CareFormTypesIndexPage"),
);
const CareFormTypeDetailPage = React.lazy(
	() => import("@/care-forms/pages/CareFormTypeDetailPage"),
);
const CareFormIndexPage = React.lazy(
	() => import("@/care-forms/pages/CareFormIndexPage"),
);
const CareFormDetailPage = React.lazy(
	() => import("@/care-forms/pages/CareFormDetailPage"),
);

const CaregiversPage = React.lazy(
	() => import("@/caregivers/pages/CaregiversIndexPage"),
);
const CaregiverProfilePage = React.lazy(
	() => import("@/caregivers/pages/CaregiverProfilePage"),
);
const CaregiverSchedulePage = React.lazy(
	() => import("@/caregivers/pages/CaregiverSchedulePage"),
);
const ChatLayout = React.lazy(() => import("@/chat/pages/ChatLayout"));
const ChatPage = React.lazy(() => import("@/chat/pages/ChatPage"));
const ClientsPage = React.lazy(
	() => import("@/clients/pages/ClientsIndexPage"),
);
const ClientProfilePage = React.lazy(
	() => import("@/clients/pages/ClientProfilePage"),
);
const ClientSchedulePage = React.lazy(
	() => import("@/clients/pages/ClientSchedulePage"),
);
const DocsPage = React.lazy(() => import("@/docs/pages/DocsPage"));
const DashboardPage = React.lazy(
	() => import("@/dashboard/pages/DashboardPage"),
);
const DashboardRollupPage = React.lazy(
	() => import("@/dashboard/pages/DashboardRollupPage"),
);
const DashboardAdminPage = React.lazy(
	() => import("@/dashboard/pages/DashboardAdminPage"),
);
const AutomatedImportPage = React.lazy(
	() => import("@/dashboard/pages/AutomatedImportPage"),
);
const FileExportsPage = React.lazy(
	() => import("@/file-import-export/pages/FilesExportPage"),
);
const FileImportsPage = React.lazy(
	() => import("@/file-import-export/pages/FilesImportPage"),
);
const FileImportRowsPage = React.lazy(
	() => import("@/file-import-export/pages/FileImportRowsPage"),
);
const FileExportTypesPage = React.lazy(
	() => import("@/file-import-export/pages/FileExportTypesPage"),
);
const FileImportTypesPage = React.lazy(
	() => import("@/file-import-export/pages/FileImportTypesPage"),
);
const NotificationsPage = React.lazy(
	() => import("@/shifts/pages/NotificationsPage"),
);
const PayPeriodLockTimesPage = React.lazy(
	() => import("@/payroll/pages/PayPeriodLockTimesPage"),
);
const PayerSourcesPage = React.lazy(
	() => import("@/payer-sources/pages/PayerSourcesPage"),
);
const PayerSourceDetailPage = React.lazy(
	() => import("@/payer-sources/pages/PayerSourceDetailPage"),
);
const VAMedicalCentersPage = React.lazy(
	() => import("@/map/pages/VAMedicalCentersPage"),
);
const VAMedicalCenterDetailPage = React.lazy(
	() => import("@/map/pages/VAMedicalCenterDetailPage"),
);
const RolesPage = React.lazy(() => import("@/users/pages/RolesPage"));
const FeatureFlagsPage = React.lazy(
	() => import("@/feature-flags/pages/FeatureFlagsPage"),
);
const UsersPage = React.lazy(() => import("@/users/pages/users-page"));
const UserDetailPage = React.lazy(
	() => import("@/users/pages/user-detail-page"),
);
const ReportsIndexPage = React.lazy(
	() => import("@/reports/pages/ReportsIndexPage"),
);
const CcwReportPage = React.lazy(() => import("@/reports/pages/CcwReportPage"));
const AuthorizationReviewPage = React.lazy(
	() => import("@/reports/pages/AuthorizationReviewPage"),
);
const ADPImportedEmployeesReportPage = React.lazy(
	() => import("@/payroll/pages/ADPImportedEmployeesReportPage"),
);
const BillingReportPage = React.lazy(
	() => import("@/billing/pages/BillingReportPage"),
);
const CaregiverCashAdvancesReportPage = React.lazy(
	() => import("@/caregivers/pages/CaregiverCashAdvancesReportPage"),
);
const CaregiverChangesReportPage = React.lazy(
	() => import("@/caregivers/pages/CaregiverChangesReportPage"),
);
const CaregiverFilesReportPage = React.lazy(
	() => import("@/caregivers/pages/CaregiverFilesReportPage"),
);
const CaregiverJournalsReportPage = React.lazy(
	() => import("@/caregivers/pages/CaregiverJournalsReportPage"),
);
const CaregiverPayrollReportPage = React.lazy(
	() => import("@/caregivers/pages/CaregiverPayrollReportPage"),
);
const CareSheetFilesReportPage = React.lazy(
	() => import("@/care-forms/pages/CareSheetFilesReportPage"),
);
const ClientAuthorizationsReportPage = React.lazy(
	() => import("@/clients/pages/ClientAuthorizationsReportPage"),
);
const ClientFilesReportPage = React.lazy(
	() => import("@/clients/pages/ClientFilesReportPage"),
);
const ClientJournalsReportPage = React.lazy(
	() => import("@/clients/pages/ClientJournalsReportPage"),
);
const IntegrationTransactionRecordsReportPage = React.lazy(
	() =>
		import(
			"@/integration-transactions/pages/IntegrationTransactionRecordsReportPage"
		),
);
const IntegrationTransactionsReportPage = React.lazy(
	() =>
		import(
			"@/integration-transactions/pages/IntegrationTransactionsReportPage"
		),
);
const NurseAssessmentsReportPage = React.lazy(
	() => import("@/clients/pages/NurseAssessmentsReportPage"),
);

const ClientMobileAppHomePage = React.lazy(
	() => import("@/client-mobile-app/pages/home-page/ClientMobileAppHomePage"),
);

const MapPage = React.lazy(() => import("@/map/pages/MapPage"));
const TicketsBoardPage = React.lazy(
	() => import("@/tickets/pages/tickets-board-page"),
);
const TicketsDetailPage = React.lazy(
	() => import("@/tickets/pages/tickets-detail-page"),
);
const TicketsProjectDetailPage = React.lazy(
	() => import("@/tickets/pages/ticket-project-detail-page"),
);
const TicketsCycleDetailPage = React.lazy(
	() => import("@/tickets/pages/ticket-cycle-detail-page"),
);
const TicketsArchivedPage = React.lazy(
	() => import("@/tickets/pages/tickets-archived-page"),
);
const TicketsImportPage = React.lazy(
	() => import("@/tickets/pages/tickets-import-page"),
);
